import { useRef, useState, useMemo, useEffect, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { useShippingFinancialStatementPermissionFlags } from '@/lib/navigation/shippingFinancialStatementPermissions'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  sessionListStateKey,
  syncSearchInputRef,
  useSessionListState,
} from '@/hooks/useSessionListState'

type TabValue = 'all' | 'trash'

export default function ShippingLinesPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()
  const financialNavFlags = useShippingFinancialStatementPermissionFlags()

  useSessionListState({
    key: sessionListStateKey('shipping-lines'),
    values: {
      activeTab,
      searchQuery,
    },
    onRestore: (raw) => {
      if (raw.activeTab === 'all' || raw.activeTab === 'trash') {
        setActiveTab(raw.activeTab)
      }
      if (typeof raw.searchQuery === 'string') {
        setSearchQuery(raw.searchQuery)
        syncSearchInputRef(searchRef, raw.searchQuery)
      }
    },
  })

  const statusFilterOptions = useMemo(
    () => [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: {
          name: 'Name',
          withSort: true,
          maxWidth: '100px',
        },
        short_name: {
          name: 'Short Name',
          maxWidth: '100px',
        },
        address: {
          name: 'Address',
          withSort: true,
          maxWidth: '250px',
        },
        contact_name: {
          name: 'Contact Person',
          withSort: true,
          maxWidth: '180px',
        },
        contact_mobile: {
          name: 'Mobile Number',
          withSort: false,
          maxWidth: '150px',
        },
      },
      dataSource: activeTab === 'trash' ? '/archived/shipping-lines' : '/shipping-lines',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No Shipping Lines found',
      otherActions: financialNavFlags.canViewFinancialStatementsPage
        ? [
            {
              name: 'Financial Statements',
              onClick: (row: any) => {
                navigate(`/shipping-lines/financial-statements/${row.id}`)
              },
            },
          ]
        : [],
    }),
    [activeTab, financialNavFlags.canViewFinancialStatementsPage, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false
    }),
    [access]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        // backend bulk restore path (as per your routes)
        url = '/shipping-lines/bulk/restore'
        successMessage = 'Shipping Lines restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/shipping-lines/bulk/force-delete'
            : '/shipping-lines/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Shipping Lines permanently deleted successfully' : 'Shipping Lines moved to trash successfully'
      } else {
        showWarning('Unsupported bulk action')
        setIsBulkProcessing(false)
        return
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Shipping Lines
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage shipping line records
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/shipping-lines"
                      createLabel="Create New Shipping Line"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by name, email..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/shipping-lines/${id}`)}
                  filterOptions={{
                    active: statusFilterOptions,
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
